import type { FormEvent, ReactNode, RefObject, JSX } from 'react'

import { clsx } from 'clsx'

import { useCommunityConfig } from '../../CommunityConfig'
import { EngagementAvatar } from '../EngagementAvatar'
import { EngagementReplyFormFooter } from '../EngagementReplyFormFooter'
import type { EngagementReplyFormAsset } from '../EngagementReplyFormFooter'
import { EngagementReplyFormItem } from '../EngagementReplyFormItem'
import { EngagementReplyProcessing } from '../EngagementReplyProcessing'
import { ReplyForm } from '../ReplyForm'

import type { EngagementReplyFormChannel } from './hooks/types'
import type { EngagementReplyController } from './types'

import styles from './EngagementReplyForm.module.css'

type EngagementReplyFormProps = {
  /** Reply state + actions. Both feature reply hooks satisfy this contract. */
  reply: EngagementReplyController
  channel: EngagementReplyFormChannel
  textareaRef: RefObject<HTMLTextAreaElement>
  /** Whether the thread is expanded (the form only shows when expanded). */
  expanded: boolean
  /** Whether the channel allows replying. */
  canReply: boolean
  id?: string
  /** Accessible label for the form, e.g. "Reply to comment from @user". */
  ariaLabel: string
  /** Header row above the textarea (the "Replying to @user" label + AI chips). */
  suggestions: ReactNode
  /** Textarea placeholder. Defaults to the generic reply prompt. */
  placeholder?: string
  /** Assets attached to the engagement, used to prefill the "Start a Post" composer. */
  assets: EngagementReplyFormAsset[]
  /** Analytics CTA id for the "Start a Post" composer trigger. */
  cta: CTA
  /** Test id for the in-flight "Sending…" row (e.g. comment-item / mention-item). */
  processingTestId?: string
  /** Feature-specific actions appended to the footer's composer icon group. */
  footerExtraActions?: ReactNode
  /** Extra class on the form item, for per-feature layout overrides. */
  className?: string
}

/**
 * Reply area shared by comments and mentions. Owns the processing-vs-form-vs-
 * nothing gating: shows the "Sending…" banner while a reply is in flight,
 * otherwise the reply form when the thread is expanded and replying is allowed.
 * Reply state/actions come in via the `reply` controller; the thin
 * `CommentReplyForm` / `MentionReplyForm` wrappers read context and feed the
 * feature-specific slots (suggestions, aria label, placeholder, assets, cta).
 */
export const EngagementReplyForm = ({
  reply,
  channel,
  textareaRef,
  expanded,
  canReply,
  id,
  ariaLabel,
  suggestions,
  placeholder,
  assets,
  cta,
  processingTestId,
  footerExtraActions,
  className,
}: EngagementReplyFormProps): JSX.Element | null => {
  const {
    config: { getCharacterLimit },
  } = useCommunityConfig()

  const isProcessing = reply.status === 'processing'
  const shouldShowForm = expanded && canReply && !isProcessing

  // Always show the processing state, even if expanded/canReply change.
  if (!shouldShowForm && !isProcessing) {
    return null
  }

  const maxChars = getCharacterLimit(channel)

  return (
    <>
      {isProcessing && (
        <EngagementReplyProcessing
          key="processing"
          channel={channel}
          text={reply.submittingText || 'Processing your reply...'}
          data-testid={processingTestId}
        />
      )}

      {shouldShowForm && (
        <EngagementReplyFormView
          key="form"
          id={id}
          channelAvatar={channel.avatar}
          channelName={channel.name}
          error={reply.error}
          suggestions={suggestions}
          ariaLabel={ariaLabel}
          onSubmit={(e) => {
            void reply.submit(e)
          }}
          className={className}
        >
          <ReplyForm.Field
            id={id}
            textareaRef={textareaRef}
            replyText={reply.text}
            setReplyText={reply.setText}
            error={reply.error}
            ariaLabel={ariaLabel}
            insertedRange={reply.insertedRange}
            onHighlightComplete={reply.clearInsertedRange}
            placeholder={placeholder}
            maxLength={maxChars}
            service={channel.service}
          />

          <EngagementReplyFormFooter
            maxChars={maxChars}
            disabled={!reply.text.trim()}
            label={reply.error ? 'Try Again' : 'Reply'}
            channelId={channel.id}
            channelService={channel.service}
            textareaRef={textareaRef}
            replyText={reply.text}
            setReplyText={reply.setText}
            savedRepliesPopoverRef={reply.savedRepliesRef}
            handleInsertSavedReply={reply.insertSavedReply}
            emojiPickerRef={reply.emojiPickerRef}
            assets={assets}
            cta={cta}
            extraActions={footerExtraActions}
          />
        </EngagementReplyFormView>
      )}
    </>
  )
}

EngagementReplyForm.displayName = 'EngagementReplyForm'

type EngagementReplyFormViewProps = {
  id?: string
  channelAvatar: string
  channelName: string
  error?: string
  suggestions: ReactNode
  ariaLabel: string
  onSubmit: (event: FormEvent<HTMLFormElement>) => void
  className?: string
  children: ReactNode
}

/**
 * The comment and mention layout around the reply form: the channel avatar
 * column, the error, and the suggestions in a `ReplyForm.Header` sit beside
 * and above a `ReplyForm.Root` on the subtle surface. Children are the
 * form's parts.
 */
export const EngagementReplyFormView = ({
  id,
  channelAvatar,
  channelName,
  error,
  suggestions,
  ariaLabel,
  onSubmit,
  className,
  children,
}: EngagementReplyFormViewProps): JSX.Element => {
  // Connector height accounts for the always-present header bar (height +
  // padding + gaps) above the textarea.
  const avatarStyle: React.CSSProperties & Record<`--${string}`, string> = {
    '--connector-height': '64px',
  }

  return (
    <EngagementReplyFormItem
      avatar={
        <EngagementAvatar
          hasParent={true}
          avatar={channelAvatar}
          username={channelName}
          showConnector={false}
          style={avatarStyle}
        />
      }
      error={error}
      errorId={id ? `${id}-error` : undefined}
      suggestions={<ReplyForm.Header>{suggestions}</ReplyForm.Header>}
      className={clsx(styles.replyTextArea, className)}
    >
      <ReplyForm.Root
        surface="subtle"
        ariaLabel={ariaLabel}
        onSubmit={onSubmit}
        className={styles.form}
      >
        {children}
      </ReplyForm.Root>
    </EngagementReplyFormItem>
  )
}
